import React from 'react';

import {
  STEP_TYPE_TO_USER_FACING_NAME,
  STEP_TYPE_TO_GROUPS,
} from 'graphql-client/__generated/GeneratedEnumMaps';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import isStepTypeEnabledForUser from 'pipelines/PipelineCapabilities';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

interface StepTypeSelectorProps {
  /**
   * Filter which step types are included in the menu.
   *
   * Step types for which the user lacks the required capabilities are automatically hidden, regardless of
   * `filterStepTypes`. See `isStepTypeEnabledForUser`.
   */
  filterStepTypes?: (stepType: PipelineStepType) => boolean;
  selectedStepType?: PipelineStepType | null;
  onSelect: (stepType: PipelineStepType) => void;
}

/**
 * Function for organizing step types into groups.
 *
 * @param stepTypes list of step types to sort by group
 * @returns a record of groups and their corresponding step types
 */
function sortStepTypesByGroup(stepTypes: Array<PipelineStepType>): Record<string, Array<PipelineStepType>> {
  // TODO: Make groups an enum so we can have universal ordering in all uses.
  const stepTypesByGroup: Record<string, Array<PipelineStepType>> = {};
  for (const stepType of stepTypes) {
    if (STEP_TYPE_TO_GROUPS[stepType].length === 0) {
      if (stepTypesByGroup.Other === undefined) {
        stepTypesByGroup.Other = [];
      }
      stepTypesByGroup.Other.push(stepType);
    }
    for (const group of STEP_TYPE_TO_GROUPS[stepType]) {
      if (stepTypesByGroup[group] === undefined) {
        stepTypesByGroup[group] = [];
      }
      stepTypesByGroup[group].push(stepType);
    }
  }
  const sortedGroups = Object.keys(stepTypesByGroup)
    .filter((group) => group !== 'Other')
    .sort((a, b) => a.localeCompare(b));
  if (stepTypesByGroup.Other) {
    sortedGroups.push('Other');
  }
  const sortedStepTypesByGroup: Record<string, Array<PipelineStepType>> = {};
  for (const group of sortedGroups) {
    sortedStepTypesByGroup[group] = stepTypesByGroup[group].toSorted((a, b) =>
      STEP_TYPE_TO_USER_FACING_NAME[a].localeCompare(STEP_TYPE_TO_USER_FACING_NAME[b])
    );
  }
  return sortedStepTypesByGroup;
}

/**
 * Selector for selecting pipeline step types.
 */
export default function StepTypeSelector({
  filterStepTypes,
  selectedStepType,
  onSelect,
}: StepTypeSelectorProps): JSX.Element {
  const stepTypes = Object.values(PipelineStepType)
    .filter((stepType) => filterStepTypes?.(stepType) ?? true)
    .filter((stepType) => isStepTypeEnabledForUser(stepType));
  const stepTypesByGroup = sortStepTypesByGroup(stepTypes as Array<PipelineStepType>);

  const optionDatas: Array<OptionData> = Object.entries(stepTypesByGroup)
    .filter(([group, stepTypes]) => stepTypes.length > 0)
    .flatMap(([group, stepTypes]) => [
      {name: group, disabled: true, isHeader: true},
      ...stepTypes.map((stepType) => ({name: STEP_TYPE_TO_USER_FACING_NAME[stepType], value: stepType})),
    ]);

  return (
    <SelectDropdown
      menu={
        <SearchableMenu
          onSelect={onSelect}
          optionDatas={optionDatas}
          selectedValue={selectedStepType}
          width={MenuWidth.FULL}
        />
      }
      displayName={
        optionDatas.length === 0
          ? 'No step types are available'
          : selectedStepType
            ? STEP_TYPE_TO_USER_FACING_NAME[selectedStepType]
            : 'Select step type'
      }
    />
  );
}
